import React from 'react';
import { Box, Paper, Typography } from '@mui/material';

interface StatTileProps {
  icon: React.ReactNode;
  value: string | number;
  label: string;
  /** Accent colour for the icon and value text. */
  color: string;
  /** Translucent background derived from colour. */
  bgColor: string;
}

/**
 * A compact stats tile: coloured icon on the left, big number + label on the right.
 * Used in the right-hand sidebar column on desktop and stacked on mobile.
 */
const StatTile: React.FC<StatTileProps> = ({ icon, value, label, color, bgColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 3,
      bgcolor: bgColor,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      border: `1px solid ${color}30`,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 8px 24px ${color}22`,
      },
    }}
  >
    <Box
      sx={{
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: 2,
        bgcolor: `${color}18`,
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography
        variant="h5"
        fontWeight="900"
        sx={{ color, lineHeight: 1, letterSpacing: '-0.5px' }}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </Box>
  </Paper>
);

export default StatTile;
